import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  listMembers,
  getMember,
  updateMember,
  createMemberLegacy,
  listMemberGenders,
  listMemberTitles,
  listExpiredMembers,
  inactivateMember,
  reactivateMember,
  getMemberClubRequest,
  requestMemberClubChange,
  createStaffMemberRegistration,
  previewStaffMemberRegistration,
} from "../../services/memberService";
import { listMemberTypes } from "../../services/idCardService";
import { listClubs } from "../../services/clubService";
import { listFees } from "../../services/feeService";
import { listMemberRegistrationProcesses, getMemberRegistrationProcess } from "../../services/registrationProcessService";
import {
  searchMemberRegistrationPayment,
  confirmMemberRegistrationPayment,
  getMemberShroffVoucherPdf,
  listMyManualPaymentSubmissions,
  uploadManualPaymentReceipt,
  getManualPaymentReceiptFile,
  listPayments,
} from "../../services/paymentService";
import { getMemberBookings } from "../../services/bookingService";
import { getMemberAttendances } from "../../services/attendanceService";
import { storageUrl } from "../../services/api";
import {
  TOKENS,
  Icon,
  ICONS,
  Avatar,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
  DEFAULT_PAGE_SIZE,
} from "../../components/common/DashboardUI";
import ReceiptFileInput, { validateReceiptFile } from "../../components/common/ReceiptFileInput";

// ── Modal shell (matches the pattern used across the admin panel) ───────────
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

const fullName = (m) => [m?.title, m?.name_denoted_by_initials, m?.lastname].filter(Boolean).join(" ") || "—";

// Pull the top-level message and field-level `errors` map out of an axios
// error (backend Validator returns { message, errors: { field: [msg,...] } }
// on a 422).
function errInfo(err, fallback) {
  const data = err?.response?.data;
  return { message: data?.message || fallback, fields: data?.errors || {} };
}

// ── Pending Registrations tab (member registration processes) ──────────────
const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`);

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

// Read-only field grid used on the process detail page below.
function ProcessDetailGrid({ fields }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      {fields
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-gray-400">{label}</dt>
            <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
              {value}
            </dd>
          </div>
        ))}
    </dl>
  );
}

// Read-only detail page for a single member registration process — every
// attempt (pending, paid-but-unfinalized, completed), not just finalized
// members. Moved here from the old standalone "Registration Processes" admin
// page; now reachable only via the "Pending Registrations" sub-tab below.
function MemberProcessDetailPage() {
  const { processId } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    setLoading(true);
    getMemberRegistrationProcess(processId)
      .then((p) => on && setProcess(p))
      .catch((err) => on && setError(errInfo(err, "Could not load this registration process.").message))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [processId]);

  return (
    <div>
      <button
        onClick={() => navigate("/admin/members")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold mb-4 text-gray-500 hover:text-gray-800"
      >
        <Icon path={ICONS.chevron} className="w-4 h-4 rotate-90" width={2} />
        Back to Members
      </button>
      <div className="bg-white rounded-2xl shadow-sm p-6 max-w-3xl">
        <h1 className="text-lg font-bold mb-5" style={{ color: TOKENS.NAVY }}>
          Member Registration Process
        </h1>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Avatar name={process.full_name} url={storageUrl(process.photo_path)} size={56} />
              <div className="min-w-0">
                <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                  {[process.title, process.full_name].filter(Boolean).join(" ")}
                </p>
                <p className="text-xs text-gray-400 truncate">{process.email}</p>
              </div>
              <div className="ml-auto flex flex-col items-end gap-1">
                <Pill>{process.process_status}</Pill>
                <Pill>{process.payment_status}</Pill>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase text-gray-400 mb-3">Applicant</h3>
              <ProcessDetailGrid
                fields={[
                  ["NIC", process.nic_number],
                  ["Gender", process.member_gender],
                  ["Date of birth", process.date_of_birth],
                  ["Primary phone", process.primary_phone_number],
                  ["Secondary phone", process.secondary_phone_number],
                  ["Address", process.address],
                  ["Member type", process.member_type],
                  ["Coach role", process.coach_role],
                  ["Requested club", process.requested_club_name],
                  ["Linked club", process.club_name],
                  ["Club verification status", process.club_verification_status],
                  ["Email verified at", fmtDateTime(process.email_verified_at)],
                ]}
              />
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase text-gray-400 mb-3">Payment</h3>
              <ProcessDetailGrid
                fields={[
                  ["Income", process.income_description],
                  ["Ledger code", process.ledger_code],
                  ["Payment reference", process.payment_reference],
                  ["External reference", process.external_payment_reference],
                  ["Payable amount", money(process.payable_amount)],
                  ["Paid amount", money(process.paid_amount)],
                  ["Paid at", fmtDateTime(process.paid_at)],
                ]}
              />
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase text-gray-400 mb-3">Outcome</h3>
              <ProcessDetailGrid
                fields={[
                  ["Final member email", process.final_member_email],
                  ["Created", fmtDateTime(process.created_at)],
                  ["Updated", fmtDateTime(process.updated_at)],
                ]}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Confirm Shroff Payment tab (manual registration-payment reconciliation) ─
// Look up a member registration process by its payment_reference and submit
// a shroff (walk-in) counter receipt for it, instead of going through the
// CyberExt gateway. Search is gated by "view_payments"; the submit form only
// renders for "submit_manual_payment_receipts" — see
// RegistrationPaymentController::manualMemberSearch/manualMemberConfirm.
// Submitting marks the payment Paid immediately. It also queues the receipt
// for a Director/Admin to acknowledge on the Payments page's "Manual
// Approvals" tab (see ManualPaymentApprovalController) — that step is
// audit-only and doesn't gate the payment.
function ConfirmShroffPaymentTab({ canConfirm }) {
  const [reference, setReference] = useState("");
  const [searching, setSearching] = useState(false);
  const [payment, setPayment] = useState(null);
  const [searchError, setSearchError] = useState("");

  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmFieldErrors, setConfirmFieldErrors] = useState({});
  const [confirmResult, setConfirmResult] = useState(null);

  const fieldError = (key) => {
    const msgs = confirmFieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const resetConfirmForm = (p) => {
    // Left blank on purpose — the operator types the amount actually received.
    setPaidAmount("");
    setPaidDate(new Date().toISOString().slice(0, 10));
    setReceiptReference("");
    setReceiptFile(null);
    setNote("");
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirmResult(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref || searching) return;
    setSearching(true);
    setSearchError("");
    setPayment(null);
    setConfirmResult(null);
    try {
      const found = await searchMemberRegistrationPayment(ref);
      setPayment(found);
      resetConfirmForm(found);
    } catch (err) {
      setSearchError(errInfo(err, "No member registration payment found for that reference.").message);
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirming || !payment || !receiptFile) return;
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirming(true);
    try {
      const result = await confirmMemberRegistrationPayment({
        payment_reference: payment.payment_reference,
        paid_amount: paidAmount,
        paid_date: paidDate,
        receipt_reference: receiptReference,
        receipt: receiptFile,
        note: note || undefined,
      });
      toast.success(result.message || "Payment confirmed. Receipt pending director acknowledgement.");
      setConfirmResult(result);
    } catch (err) {
      const info = errInfo(err, "Could not submit this receipt.");
      setConfirmError(info.message);
      setConfirmFieldErrors(info.fields);
    } finally {
      setConfirming(false);
    }
  };

  const isPending = payment?.payment_status === "Pending";

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
          Find a registration payment
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Look up a member's registration payment by its reference number, then record a payment taken at the
          shroff counter.
        </p>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <input
            className={`${inputClass} flex-1 min-w-[220px] font-mono`}
            placeholder="Payment reference, e.g. 07301000003"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <button type="submit" disabled={searching || !reference.trim()} className={`${btnPrimary} !w-auto px-6`}>
            {searching ? "Searching…" : "Search"}
          </button>
        </form>
        {searchError && <p className="text-sm text-red-600 mt-3">{searchError}</p>}
      </div>

      {payment && (
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                {[payment.applicant?.title, payment.applicant?.full_name].filter(Boolean).join(" ")}
              </p>
              <p className="text-xs text-gray-400 truncate">{payment.applicant?.email}</p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Pill>{payment.payment_status}</Pill>
              <Pill>{payment.process_status}</Pill>
            </div>
          </div>

          <ProcessDetailGrid
            fields={[
              ["Payment reference", payment.payment_reference],
              ["Income", payment.income_description],
              ["Payable amount", money(payment.payable_amount)],
              ["Paid amount", money(payment.paid_amount)],
              ["Paid at", fmtDateTime(payment.paid_at)],
              ["Requested club", payment.applicant?.requested_club_name],
              ["NIC", payment.applicant?.nic_number],
              ["Phone", payment.applicant?.primary_phone_number],
            ]}
          />

          {!isPending ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              This payment is already <strong>{payment.payment_status}</strong> — nothing to confirm.
            </p>
          ) : !canConfirm ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              You don't have permission to confirm this payment.
            </p>
          ) : (
            <form onSubmit={handleConfirm} className="space-y-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                Submit Shroff Receipt
              </h4>
              <p className="text-xs text-gray-400 -mt-2">
                Submitting confirms this payment immediately. A Director/Admin will acknowledge the receipt
                afterward for audit purposes.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Paid amount <span className="text-gray-400 font-normal">(must match payable amount)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    required
                  />
                  {fieldError("paid_amount") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_amount")}</p>}
                </div>
                <div>
                  <label className={labelClass}>Paid date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                    required
                  />
                  {fieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_date")}</p>}
                </div>
              </div>
              <div>
                <label className={labelClass}>Receipt / slip reference</label>
                <input
                  className={inputClass}
                  value={receiptReference}
                  onChange={(e) => setReceiptReference(e.target.value)}
                  required
                />
                {fieldError("receipt_reference") && (
                  <p className="text-xs text-red-600 mt-1">{fieldError("receipt_reference")}</p>
                )}
              </div>
              <ReceiptFileInput file={receiptFile} onChange={setReceiptFile} error={fieldError("receipt")} />
              <div>
                <label className={labelClass}>
                  Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  className={`${inputClass} resize-none`}
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                {fieldError("note") && <p className="text-xs text-red-600 mt-1">{fieldError("note")}</p>}
              </div>

              {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}

              <button type="submit" disabled={confirming || !receiptFile} className={`${btnPrimary} w-full`}>
                {confirming ? "Confirming…" : "Confirm Payment"}
              </button>
            </form>
          )}

          {confirmResult && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              {confirmResult.message || "Payment confirmed. Receipt pending director acknowledgement."}
              {confirmResult.approval_id && (
                <>
                  {" "}
                  Approval ID: <span className="font-mono font-semibold">#{confirmResult.approval_id}</span> — will
                  be acknowledged on the Payments page's Manual Approvals tab.
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── My Submissions tab (this instructor's own shroff receipt submissions) ──
// Read-only: the my-submissions endpoint isn't scoped to members vs clubs —
// it returns every receipt this system user has submitted regardless of
// payable type — so a member payment submitted from this page and a club
// payment submitted from the Clubs page both show up here. Gated by
// "submit_manual_payment_receipts" (same permission needed to submit one) —
// see ManualPaymentApprovalController::mySubmissions.
function MySubmissionsTab() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [missingReceiptOnly, setMissingReceiptOnly] = useState(false);
  const [viewingReceiptId, setViewingReceiptId] = useState(null);
  const [uploadingReceiptId, setUploadingReceiptId] = useState(null);

  // Streams the scan through the authenticated receipt route — the
  // submitter's own submit_manual_payment_receipts permission is enough.
  const handleViewReceipt = async (approvalId) => {
    if (viewingReceiptId) return;
    const tab = window.open("", "_blank"); // synchronous, so the popup blocker allows it
    setViewingReceiptId(approvalId);
    try {
      const blob = await getManualPaymentReceiptFile(approvalId);
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not open this receipt.").message);
    } finally {
      setViewingReceiptId(null);
    }
  };

  // Backfills a receipt onto a submission made before receipts were required.
  const handleUploadReceipt = async (approvalId, file) => {
    const invalid = validateReceiptFile(file);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setUploadingReceiptId(approvalId);
    try {
      const result = await uploadManualPaymentReceipt(approvalId, file);
      toast.success(result.message || "Payment receipt uploaded.");
      fetchSubmissions();
    } catch (err) {
      const info = errInfo(err, "Could not upload this receipt.");
      toast.error(info.fields?.receipt?.[0] || info.message);
    } finally {
      setUploadingReceiptId(null);
    }
  };

  const fetchSubmissions = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (missingReceiptOnly) params.missing_receipt = 1;
    listMyManualPaymentSubmissions(params)
      .then(setSubmissions)
      .catch((err) => toast.error(errInfo(err, "Could not load your submissions.").message))
      .finally(() => setLoading(false));
  }, [statusFilter, missingReceiptOnly]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const { page, setPage, totalPages, pageItems } = usePagination(submissions, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: `${statusFilter}|${missingReceiptOnly}`,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select className={`${inputClass} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Acknowledged</option>
          <option value="Rejected">Rejected</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={missingReceiptOnly}
            onChange={(e) => setMissingReceiptOnly(e.target.checked)}
          />
          Missing receipt only
        </label>
      </div>
      <div className="bg-white rounded-2xl shadow-sm p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : submissions.length === 0 ? (
          <EmptyState icon={ICONS.payments} text="You haven't submitted any shroff receipts yet." />
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-left min-w-[880px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Receipt Ref</th>
                  <th className="py-2 px-3 font-medium">For</th>
                  <th className="py-2 px-3 font-medium">Amount</th>
                  <th className="py-2 px-3 font-medium">Submitted</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium">Receipt</th>
                  <th className="py-2 pl-3 font-medium">Rejection Note</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((s) => (
                  <tr key={s.approval_id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-mono text-gray-600 whitespace-nowrap">{s.receipt_reference}</td>
                    <td className="py-3 px-3 text-sm font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                      {s.payable?.member_name || s.payable?.club_name || "—"}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{money(s.amount_paid)}</td>
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(s.submitted_at)}</td>
                    <td className="py-3 px-3">
                      <Pill>{s.approval_status === "Approved" ? "Acknowledged" : s.approval_status}</Pill>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      {s.has_receipt_file ? (
                        <button
                          type="button"
                          onClick={() => handleViewReceipt(s.approval_id)}
                          disabled={viewingReceiptId === s.approval_id}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                        >
                          <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                          {viewingReceiptId === s.approval_id ? "Opening…" : "View"}
                        </button>
                      ) : (
                        <label
                          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 ${
                            uploadingReceiptId ? "opacity-50 cursor-wait" : "cursor-pointer hover:bg-amber-100"
                          }`}
                          title="No receipt attached — click to upload one (PDF or image, max 5MB)"
                        >
                          <Icon path={ICONS.plus} className="w-3.5 h-3.5" width={2} />
                          {uploadingReceiptId === s.approval_id ? "Uploading…" : "Upload receipt"}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            className="hidden"
                            disabled={uploadingReceiptId !== null}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (file) handleUploadReceipt(s.approval_id, file);
                            }}
                          />
                        </label>
                      )}
                    </td>
                    <td className="py-3 pl-3 text-sm text-gray-600 max-w-[240px] truncate" title={s.rejection_note || ""}>
                      {s.approval_status === "Rejected" ? s.rejection_note || "—" : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Member detail (read-only, own page) ─────────────────────────────────

// Payment-type badge — mirrors the `payment_for` discriminator GET
// /api/payments merges across (PaymentController::index): member/club
// registration, membership/club renewal, ID card reprint, facility booking.
const PAYMENT_TYPE_BADGE = {
  member_registration: { bg: "#ede9fe", color: "#6d28d9", label: "Registration" },
  club_registration: { bg: "#ede9fe", color: "#6d28d9", label: "Club Registration" },
  renewal: { bg: "#e0f2fe", color: "#0369a1", label: "Renewal" },
  club_renewal: { bg: "#e0f2fe", color: "#0369a1", label: "Club Renewal" },
  reprint: { bg: "#fce7f3", color: "#be185d", label: "ID Card Reprint" },
  booking: { bg: "#dbeafe", color: "#1d4ed8", label: "Booking" },
};

function PaymentTypeBadge({ type }) {
  const t = PAYMENT_TYPE_BADGE[type] || { bg: "#e5e7eb", color: "#4b5563", label: type || "—" };
  return (
    <span
      className="text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color }}
    >
      {t.label}
    </span>
  );
}

// The /api/payments merge doesn't return one unified "description" field —
// each payment type's row only carries what that resource actually has (see
// PaymentController::index's all*Payments() methods), so this assembles a
// readable line from whatever that row has rather than inventing detail
// (e.g. no facility/court name exists on a booking_payment row at all).
function paymentDescription(p) {
  const base = p.income_description || PAYMENT_TYPE_BADGE[p.payment_for]?.label || "Payment";
  if ((p.payment_for === "renewal" || p.payment_for === "club_renewal") && p.new_membership_start_date && p.new_membership_end_date) {
    return `${base} (${String(p.new_membership_start_date).slice(0, 10)} to ${String(p.new_membership_end_date).slice(0, 10)})`;
  }
  if (p.payment_for === "club_registration" && p.club_name) return `${base} — ${p.club_name}`;
  return base;
}

function SummaryStat({ icon, bg, color, label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-100 p-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: bg }}>
        <Icon path={icon} className="w-4 h-4" stroke={color} width={2} />
      </div>
      <p className="text-lg font-bold truncate" style={{ color: TOKENS.NAVY }}>
        {value}
      </p>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-[10px] text-gray-400">{sub}</p>
    </div>
  );
}

function MemberDetailPage() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");

  const canViewBookings = can("view_bookings");
  const canViewAttendance = can("view_attendance");

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState("");
  const [bookingSearch, setBookingSearch] = useState("");

  const [attendances, setAttendances] = useState([]);
  const [attendancesLoading, setAttendancesLoading] = useState(true);
  const [attendancesError, setAttendancesError] = useState("");
  const [attendanceSearch, setAttendanceSearch] = useState("");

  useEffect(() => {
    let on = true;
    setLoading(true);
    getMember(memberId)
      .then((m) => on && setMember(m))
      .catch((err) => on && setError(errInfo(err, "Could not load member details.").message))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [memberId]);

  // Every payment record tied to this member — registration, renewal, club
  // renewal, ID card reprint and facility booking payments all merged into
  // one list by GET /api/payments (PaymentController::index). Searching by
  // the member's own public code (e.g. "MEM000507") is what the backend
  // resolves back to a raw member id and OR-matches against every payment
  // type's owning member/payer column (PublicId::parseMember +
  // $terms['member_id'] in each all*Payments() query) — the most reliable
  // single search term to pull everything for one member regardless of
  // name/email/NIC changes.
  useEffect(() => {
    if (!member?.member_code) return undefined;
    let on = true;
    setPaymentsLoading(true);
    setPaymentsError("");
    listPayments({ search: member.member_code })
      .then((rows) => on && setPayments(rows))
      .catch((err) => on && setPaymentsError(errInfo(err, "Could not load payment history.").message))
      .finally(() => on && setPaymentsLoading(false));
    return () => {
      on = false;
    };
  }, [member?.member_code]);

  // Every facility booking this member has made, via GET
  // /api/members/{memberCode}/bookings (BookingController::forMember) — see
  // getMemberBookings() in bookingService.js. Skipped entirely (not just
  // hidden) when the operator lacks view_bookings, same as the card below
  // isn't rendered for them, so this never fires a request that would just
  // 403 anyway.
  useEffect(() => {
    if (!member?.member_code || !canViewBookings) return undefined;
    let on = true;
    setBookingsLoading(true);
    setBookingsError("");
    getMemberBookings(member.member_code)
      .then((rows) => on && setBookings(rows))
      .catch((err) => on && setBookingsError(errInfo(err, "Could not load booking history.").message))
      .finally(() => on && setBookingsLoading(false));
    return () => {
      on = false;
    };
  }, [member?.member_code, canViewBookings]);

  // Walk-in attendance history, via GET /api/members/{memberCode}/attendances
  // (AttendanceController::forMember) — see getMemberAttendances() in
  // attendanceService.js. Same "skip the request if the operator can't see
  // the card anyway" reasoning as the bookings effect above. Note this only
  // covers walk-in scans, not booking-linked attendance (documented on the
  // service function).
  useEffect(() => {
    if (!member?.member_code || !canViewAttendance) return undefined;
    let on = true;
    setAttendancesLoading(true);
    setAttendancesError("");
    getMemberAttendances(member.member_code)
      .then((rows) => on && setAttendances(rows))
      .catch((err) => on && setAttendancesError(errInfo(err, "Could not load attendance history.").message))
      .finally(() => on && setAttendancesLoading(false));
    return () => {
      on = false;
    };
  }, [member?.member_code, canViewAttendance]);

  // Row-wise pairing (Member Code/Gender, NIC/Primary phone, ...) matches the
  // target design's two-column layout — "Full name"/"Email"/"Status" are
  // shown in the header block above instead of repeated here.
  const fields = member
    ? [
        ["Member code", member.member_code],
        ["Gender", member.member_gender],
        ["NIC", member.nic_number],
        ["Primary phone", member.primary_phone_number],
        ["Secondary phone", member.secondary_phone_number],
        ["Date of birth", member.date_of_birth],
        ["Address", member.address],
        ["Type", member.member_type],
        ["Payment status", member.payment_status],
        ["Club", member.club_name],
        ["Card status", member.card_status],
      ].filter(([, v]) => v)
    : [];
  const STATUS_LABELS = ["Payment status", "Card status"];
  const FIELD_ICONS = {
    "Member code": ICONS.card,
    Gender: ICONS.user,
    NIC: ICONS.card,
    "Primary phone": ICONS.phone,
    "Secondary phone": ICONS.phone,
    "Date of birth": ICONS.bookings,
    Address: ICONS.home,
    Type: ICONS.tag,
    "Payment status": ICONS.payments,
    Club: ICONS.clubs,
    "Card status": ICONS.card,
  };

  const totalPayments = payments.length;
  const totalAmount = payments.reduce((sum, p) => sum + (Number(p.paid_amount ?? p.payable_amount) || 0), 0);
  const paidCount = payments.filter((p) => String(p.payment_status || "").toLowerCase().includes("paid")).length;
  const pendingCount = payments.filter((p) => String(p.payment_status || "").toLowerCase().includes("pending")).length;

  const filteredPayments = paymentSearch.trim()
    ? payments.filter((p) => {
        const q = paymentSearch.trim().toLowerCase();
        return (
          String(p.payment_reference || "").toLowerCase().includes(q) ||
          String(PAYMENT_TYPE_BADGE[p.payment_for]?.label || p.payment_for || "").toLowerCase().includes(q) ||
          paymentDescription(p).toLowerCase().includes(q) ||
          String(p.payment_status || "").toLowerCase().includes(q)
        );
      })
    : payments;

  const filteredBookings = bookingSearch.trim()
    ? bookings.filter((b) => {
        const q = bookingSearch.trim().toLowerCase();
        return (
          String(b.payment_reference || "").toLowerCase().includes(q) ||
          String(b.booking_date || "").toLowerCase().includes(q) ||
          String(b.payment_status || "").toLowerCase().includes(q)
        );
      })
    : bookings;

  const filteredAttendances = attendanceSearch.trim()
    ? attendances.filter((a) => {
        const q = attendanceSearch.trim().toLowerCase();
        return (
          String(a.status || "").toLowerCase().includes(q) ||
          String(a.scanned_by_name || "").toLowerCase().includes(q) ||
          String(a.scanned_at || "").toLowerCase().includes(q)
        );
      })
    : attendances;

  // These three histories are fetched in full (no server-side pagination on
  // these per-member endpoints) — paginate client-side over the already
  // search-filtered array.
  const paymentsPagination = usePagination(filteredPayments, { pageSize: DEFAULT_PAGE_SIZE, resetKey: paymentSearch });
  const bookingsPagination = usePagination(filteredBookings, { pageSize: DEFAULT_PAGE_SIZE, resetKey: bookingSearch });
  const attendancesPagination = usePagination(filteredAttendances, { pageSize: DEFAULT_PAGE_SIZE, resetKey: attendanceSearch });

  return (
    <div>
      <button
        onClick={() => navigate("/admin/members")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold mb-4 text-gray-500 hover:text-gray-800"
      >
        <Icon path={ICONS.chevron} className="w-4 h-4 rotate-90" width={2} />
        Back to Members
      </button>

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-6 max-w-2xl">
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-2xl shadow-sm p-6 max-w-2xl">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            {/* Member overview */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
              <h1 className="text-lg font-bold mb-5" style={{ color: TOKENS.NAVY }}>
                Member Overview
              </h1>
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Avatar name={fullName(member)} url={member.photo_url} size={56} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                        {fullName(member)}
                      </p>
                      {member.member_status && <Pill>{member.member_status} Member</Pill>}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{member.email}</p>
                  </div>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {fields.map(([label, value]) => (
                    <div key={label} className="min-w-0 flex items-start gap-2">
                      {FIELD_ICONS[label] && (
                        <Icon path={FIELD_ICONS[label]} className="w-4 h-4 mt-0.5 flex-shrink-0" stroke="#9ca3af" width={1.8} />
                      )}
                      <div className="min-w-0">
                        <dt className="text-xs text-gray-400">{label}</dt>
                        <dd className="text-base font-medium text-gray-700 truncate" style={{ color: TOKENS.NAVY }}>
                          {STATUS_LABELS.includes(label) ? <Pill>{value}</Pill> : value}
                        </dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* Payment summary */}
            <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-base font-bold mb-4" style={{ color: TOKENS.NAVY }}>
                Payment Summary
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <SummaryStat
                  icon={ICONS.bookings}
                  bg="#dbeafe"
                  color="#1d4ed8"
                  label="Total Payments"
                  value={paymentsLoading ? "…" : totalPayments}
                  sub="All Time"
                />
                <SummaryStat
                  icon={ICONS.payments}
                  bg="#dcfce7"
                  color="#15803d"
                  label="Total Amount"
                  value={paymentsLoading ? "…" : money(totalAmount)}
                  sub="All Time"
                />
                <SummaryStat
                  icon={ICONS.check}
                  bg="#dcfce7"
                  color="#15803d"
                  label="Paid"
                  value={paymentsLoading ? "…" : paidCount}
                  sub="Payments"
                />
                <SummaryStat
                  icon={ICONS.clock}
                  bg="#ffedd5"
                  color="#c2410c"
                  label="Pending"
                  value={paymentsLoading ? "…" : pendingCount}
                  sub="Payment(s)"
                />
              </div>
            </div>
          </div>

          {/* All payments */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-bold" style={{ color: TOKENS.NAVY }}>
                  All Payments
                </h3>
                <p className="text-xs text-gray-400">
                  All transactions for this member (Bookings, Registrations, Renewals &amp; more).
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <input
                  className={`${inputClass} pr-9`}
                  placeholder="Search payments…"
                  value={paymentSearch}
                  onChange={(e) => setPaymentSearch(e.target.value)}
                />
                <Icon
                  path={ICONS.search}
                  className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  stroke="#9ca3af"
                  width={2}
                />
              </div>
            </div>

            {paymentsLoading ? (
              <div className="flex items-center justify-center gap-2 py-10">
                <Spinner />
                <p className="text-sm text-gray-400">Loading payments…</p>
              </div>
            ) : paymentsError ? (
              <p className="text-sm text-red-600 py-4">{paymentsError}</p>
            ) : filteredPayments.length === 0 ? (
              <EmptyState
                icon={ICONS.payments}
                text={payments.length === 0 ? "No payment records found for this member." : "No payments match your search."}
              />
            ) : (
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 px-3 font-semibold">#</th>
                      <th className="py-2 px-3 font-semibold">Payment ID</th>
                      <th className="py-2 px-3 font-semibold">Type</th>
                      <th className="py-2 px-3 font-semibold">Description</th>
                      <th className="py-2 px-3 font-semibold">Amount</th>
                      <th className="py-2 px-3 font-semibold">Status</th>
                      <th className="py-2 px-3 font-semibold">Paid At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsPagination.pageItems.map((p, i) => (
                      <tr
                        key={`${p.payment_for}_${p.id ?? p.process_id ?? p.renewal_id ?? p.reprint_id ?? p.payment_id ?? i}`}
                        className="border-b border-gray-50 last:border-0"
                      >
                        <td className="py-3 px-3 text-gray-400">{(paymentsPagination.page - 1) * DEFAULT_PAGE_SIZE + i + 1}</td>
                        <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                          {p.payment_reference || "—"}
                        </td>
                        <td className="py-3 px-3">
                          <PaymentTypeBadge type={p.payment_for} />
                        </td>
                        <td className="py-3 px-3 text-gray-600">{paymentDescription(p)}</td>
                        <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                          {money(p.paid_amount ?? p.payable_amount)}
                        </td>
                        <td className="py-3 px-3">
                          <Pill>{p.payment_status}</Pill>
                        </td>
                        <td className="py-3 px-3 text-gray-500 whitespace-nowrap">{p.paid_at ? fmtDateTime(p.paid_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!paymentsLoading && !paymentsError && payments.length > 0 && (
              <p className="text-xs text-gray-400 mt-4">
                Showing {filteredPayments.length} of {payments.length} payment{payments.length === 1 ? "" : "s"}.
              </p>
            )}
            {paymentsPagination.totalPages > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Pagination page={paymentsPagination.page} totalPages={paymentsPagination.totalPages} onPageChange={paymentsPagination.setPage} />
              </div>
            )}
          </div>

          {/* Bookings — only rendered at all when the operator can see it,
              same as the request behind it is skipped entirely (see the
              effect above) rather than firing and 403ing. */}
          {canViewBookings && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-base font-bold" style={{ color: TOKENS.NAVY }}>
                    Bookings
                  </h3>
                  <p className="text-xs text-gray-400">Facility bookings made by or for this member.</p>
                </div>
                <div className="relative w-full sm:w-64">
                  <input
                    className={`${inputClass} pr-9`}
                    placeholder="Search bookings…"
                    value={bookingSearch}
                    onChange={(e) => setBookingSearch(e.target.value)}
                  />
                  <Icon
                    path={ICONS.search}
                    className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    stroke="#9ca3af"
                    width={2}
                  />
                </div>
              </div>

              {bookingsLoading ? (
                <div className="flex items-center justify-center gap-2 py-10">
                  <Spinner />
                  <p className="text-sm text-gray-400">Loading bookings…</p>
                </div>
              ) : bookingsError ? (
                <p className="text-sm text-red-600 py-4">{bookingsError}</p>
              ) : filteredBookings.length === 0 ? (
                <EmptyState
                  icon={ICONS.bookings}
                  text={bookings.length === 0 ? "No bookings found for this member." : "No bookings match your search."}
                />
              ) : (
                <div className="overflow-x-auto -mx-2 px-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                        <th className="py-2 px-3 font-semibold">#</th>
                        <th className="py-2 px-3 font-semibold">Booking Date</th>
                        <th className="py-2 px-3 font-semibold">Slots</th>
                        <th className="py-2 px-3 font-semibold">Total Fee</th>
                        <th className="py-2 px-3 font-semibold">Payment Reference</th>
                        <th className="py-2 px-3 font-semibold">Payment Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingsPagination.pageItems.map((b, i) => (
                        <tr key={b.id ?? i} className="border-b border-gray-50 last:border-0">
                          <td className="py-3 px-3 text-gray-400">{(bookingsPagination.page - 1) * DEFAULT_PAGE_SIZE + i + 1}</td>
                          <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                            {b.booking_date || "—"}
                          </td>
                          <td className="py-3 px-3 text-gray-600">{b.total_slots ?? "—"}</td>
                          <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                            {money(b.paid_amount ?? b.payable_amount)}
                          </td>
                          <td className="py-3 px-3 text-gray-500 whitespace-nowrap">{b.payment_reference || "—"}</td>
                          <td className="py-3 px-3">
                            <Pill>{b.payment_status}</Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!bookingsLoading && !bookingsError && bookings.length > 0 && (
                <p className="text-xs text-gray-400 mt-4">
                  Showing {filteredBookings.length} of {bookings.length} booking{bookings.length === 1 ? "" : "s"}.
                </p>
              )}
              {bookingsPagination.totalPages > 1 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <Pagination page={bookingsPagination.page} totalPages={bookingsPagination.totalPages} onPageChange={bookingsPagination.setPage} />
                </div>
              )}
            </div>
          )}

          {/* Attendance — walk-in scans only, see getMemberAttendances() in
              attendanceService.js for why booking-linked attendance isn't
              included here. */}
          {canViewAttendance && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-base font-bold" style={{ color: TOKENS.NAVY }}>
                    Attendance
                  </h3>
                  <p className="text-xs text-gray-400">Walk-in scans for this member (excludes booking-linked check-ins).</p>
                </div>
                <div className="relative w-full sm:w-64">
                  <input
                    className={`${inputClass} pr-9`}
                    placeholder="Search attendance…"
                    value={attendanceSearch}
                    onChange={(e) => setAttendanceSearch(e.target.value)}
                  />
                  <Icon
                    path={ICONS.search}
                    className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    stroke="#9ca3af"
                    width={2}
                  />
                </div>
              </div>

              {attendancesLoading ? (
                <div className="flex items-center justify-center gap-2 py-10">
                  <Spinner />
                  <p className="text-sm text-gray-400">Loading attendance…</p>
                </div>
              ) : attendancesError ? (
                <p className="text-sm text-red-600 py-4">{attendancesError}</p>
              ) : filteredAttendances.length === 0 ? (
                <EmptyState
                  icon={ICONS.attendance}
                  text={attendances.length === 0 ? "No attendance records found for this member." : "No records match your search."}
                />
              ) : (
                <div className="overflow-x-auto -mx-2 px-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                        <th className="py-2 px-3 font-semibold">#</th>
                        <th className="py-2 px-3 font-semibold">Scanned At</th>
                        <th className="py-2 px-3 font-semibold">Scanned Out At</th>
                        <th className="py-2 px-3 font-semibold">Duration</th>
                        <th className="py-2 px-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendancesPagination.pageItems.map((a, i) => (
                        <tr key={a.id ?? i} className="border-b border-gray-50 last:border-0">
                          <td className="py-3 px-3 text-gray-400">{(attendancesPagination.page - 1) * DEFAULT_PAGE_SIZE + i + 1}</td>
                          <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                            {a.scanned_at ? fmtDateTime(a.scanned_at) : "—"}
                          </td>
                          <td className="py-3 px-3 text-gray-500 whitespace-nowrap">
                            {a.scanned_out_at ? fmtDateTime(a.scanned_out_at) : "—"}
                          </td>
                          <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{a.duration_time || "—"}</td>
                          <td className="py-3 px-3">
                            <Pill>{a.status}</Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!attendancesLoading && !attendancesError && attendances.length > 0 && (
                <p className="text-xs text-gray-400 mt-4">
                  Showing {filteredAttendances.length} of {attendances.length} record{attendances.length === 1 ? "" : "s"}.
                </p>
              )}
              {attendancesPagination.totalPages > 1 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <Pagination page={attendancesPagination.page} totalPages={attendancesPagination.totalPages} onPageChange={attendancesPagination.setPage} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Deactivate member (reason optional, matches the backend's `nullable`
// validation) ────────────────────────────────────────────────────────────
function InactivateMemberModal({ member, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const r = await inactivateMember(member.id, reason.trim());
      toast.success(r?.message || "Member inactivated.");
      onDone();
    } catch (err) {
      setError(errInfo(err, "Could not inactivate this member.").message);
      setSaving(false);
    }
  };

  return (
    <Modal title="Deactivate Member" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-500">
          <span className="font-semibold">{fullName(member)}</span> will be marked Inactive and their portal
          session ended immediately. They can be reactivated later.
        </p>
        <div>
          <label className={labelClass}>Reason (optional)</label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className={`${btnSecondary} flex-1`}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
            {saving ? "Deactivating…" : "Deactivate"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Change club membership (Independent ⇄ Club Student) ─────────────────
// Loads the member's current type + any pending club request first
// (getMemberClubRequest), since the list row's member_type can lag behind a
// request a primary coach has since accepted. Independent → Club Student
// only sends a request the club's primary coach accepts from the portal;
// Club Student → Independent applies immediately.
function ChangeMemberClubModal({ member, clubs, onClose, onDone }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [clubId, setClubId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    getMemberClubRequest(member.id)
      .then((data) => on && setStatus(data))
      .catch((err) => on && setLoadError(errInfo(err, "Could not load this member's club status.").message))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [member.id]);

  const memberType = status?.member_type;
  const pending = status?.pending_request;
  const currentClubName = status?.club_id
    ? clubs.find((c) => Number(c.id) === Number(status.club_id))?.club_name || member.club_name || status.club_code
    : null;

  const submit = async (payload, fallback) => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const r = await requestMemberClubChange(member.id, payload);
      toast.success(r?.message || "Membership updated.");
      onDone();
    } catch (err) {
      setError(errInfo(err, fallback).message);
      setSaving(false);
    }
  };

  const handleRequestClub = (e) => {
    e.preventDefault();
    if (!clubId) return;
    submit({ member_type: "Club Student", club_id: Number(clubId) }, "Could not send the club request.");
  };

  const handleMakeIndependent = () => {
    const message = pending
      ? `Make ${fullName(member)} independent? Their pending request for ${pending.club_name} will be cancelled.`
      : `Make ${fullName(member)} independent? They will be removed from ${currentClubName || "their club"} immediately.`;
    if (!window.confirm(message)) return;
    submit({ member_type: "Independent" }, "Could not change this member to independent.");
  };

  return (
    <Modal title="Change Club Membership" onClose={onClose}>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Spinner />
          <p className="text-sm text-gray-400">Loading club status…</p>
        </div>
      ) : loadError ? (
        <div className="space-y-4">
          <p className="text-sm text-red-600">{loadError}</p>
          <button type="button" onClick={onClose} className={`${btnSecondary} w-full`}>
            Close
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-1">
            <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
              {fullName(member)} <span className="font-mono text-xs text-gray-400">{status?.member_code}</span>
            </p>
            <p className="text-sm text-gray-600">
              Current type: <span className="font-semibold">{memberType || "—"}</span>
              {memberType === "Club Student" && currentClubName ? ` — ${currentClubName}` : ""}
            </p>
            {pending && (
              <p className="text-sm text-amber-700">
                Pending request to join <span className="font-semibold">{pending.club_name}</span> — waiting for the
                club's primary coach to accept.
              </p>
            )}
          </div>

          {memberType === "Coach" ? (
            <p className="text-sm text-gray-500">A coach's club membership can't be changed here.</p>
          ) : memberType === "Independent" && !pending ? (
            <form onSubmit={handleRequestClub} className="space-y-4">
              <div>
                <label className={labelClass}>Request to join club</label>
                <select className={inputClass} value={clubId} onChange={(e) => setClubId(e.target.value)} autoFocus>
                  <option value="">Select a club…</option>
                  {clubs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.club_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  The member stays Independent until the club's primary coach accepts the request.
                </p>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={onClose} className={`${btnSecondary} flex-1`}>
                  Cancel
                </button>
                <button type="submit" disabled={saving || !clubId} className={`${btnPrimary} flex-1`}>
                  {saving ? "Sending…" : "Send Club Request"}
                </button>
              </div>
            </form>
          ) : memberType === "Club Student" || pending ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {pending
                  ? "To stop this request, change the member back to Independent."
                  : "Changing to Independent removes the club link immediately. To move them to a different club, make them Independent first, then request the new club."}
              </p>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={onClose} className={`${btnSecondary} flex-1`}>
                  Cancel
                </button>
                <button type="button" onClick={handleMakeIndependent} disabled={saving} className={`${btnPrimary} flex-1`}>
                  {saving ? "Updating…" : pending ? "Cancel Request" : "Make Independent"}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">This member type can't be changed here.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Member edit form ────────────────────────────────────────────────────
function EditMemberForm({ member, genders, titles, onCancel, onSaved }) {
  const [title, setTitle] = useState(member.title || "");
  const [initials, setInitials] = useState(member.initials || "");
  const [nameDenoted, setNameDenoted] = useState(member.name_denoted_by_initials || "");
  const [lastname, setLastname] = useState(member.lastname || "");
  const [genderId, setGenderId] = useState(member.member_gender_id || genders[0]?.category_id || "");
  const [nic, setNic] = useState(member.nic_number || "");
  const [primaryPhone, setPrimaryPhone] = useState(member.primary_phone_number || "");
  const [secondaryPhone, setSecondaryPhone] = useState(member.secondary_phone_number || "");
  const [dob, setDob] = useState(member.date_of_birth || "");
  const [address, setAddress] = useState(member.address || "");
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setFieldErrors({});
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("initials", initials);
      formData.append("name_denoted_by_initials", nameDenoted);
      formData.append("lastname", lastname);
      formData.append("member_gender_id", genderId);
      if (nic) formData.append("nic", nic);
      formData.append("primary_phone", primaryPhone);
      if (secondaryPhone) formData.append("secondary_phone", secondaryPhone);
      if (dob) formData.append("date_of_birth", dob);
      formData.append("address", address);
      if (photoFile) formData.append("photo", photoFile);

      const updated = await updateMember(member.id, formData);
      toast.success("Member updated.");
      onSaved(updated);
    } catch (err) {
      const info = errInfo(err, "Could not update the member.");
      setError(info.message);
      setFieldErrors(info.fields);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Title</label>
          <select className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} required>
            {titles.length === 0 && <option value={title}>{title}</option>}
            {titles.map((t) => (
              <option key={t.category_id} value={t.description}>
                {t.description}
              </option>
            ))}
          </select>
          {fieldError("title") && <p className="text-xs text-red-600 mt-1">{fieldError("title")}</p>}
        </div>
        <div>
          <label className={labelClass}>Gender</label>
          <select className={inputClass} value={genderId} onChange={(e) => setGenderId(e.target.value)} required>
            {genders.map((g) => (
              <option key={g.category_id} value={g.category_id}>
                {g.description}
              </option>
            ))}
          </select>
          {fieldError("member_gender_id") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("member_gender_id")}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>
          Initials <span className="text-gray-400 font-normal">(e.g. A.B.C.)</span>
        </label>
        <input className={inputClass} value={initials} onChange={(e) => setInitials(e.target.value)} required />
        {fieldError("initials") && <p className="text-xs text-red-600 mt-1">{fieldError("initials")}</p>}
      </div>

      <div>
        <label className={labelClass}>Name denoted by initials</label>
        <input
          className={inputClass}
          value={nameDenoted}
          onChange={(e) => setNameDenoted(e.target.value)}
          required
        />
        {fieldError("name_denoted_by_initials") && (
          <p className="text-xs text-red-600 mt-1">{fieldError("name_denoted_by_initials")}</p>
        )}
      </div>

      <div>
        <label className={labelClass}>Last name</label>
        <input className={inputClass} value={lastname} onChange={(e) => setLastname(e.target.value)} required />
        {fieldError("lastname") && <p className="text-xs text-red-600 mt-1">{fieldError("lastname")}</p>}
      </div>

      <div>
        <label className={labelClass}>NIC</label>
        <input className={inputClass} value={nic} onChange={(e) => setNic(e.target.value)} />
        {fieldError("nic") && <p className="text-xs text-red-600 mt-1">{fieldError("nic")}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Primary phone</label>
          <input
            className={inputClass}
            value={primaryPhone}
            onChange={(e) => setPrimaryPhone(e.target.value)}
            required
          />
          {fieldError("primary_phone") && <p className="text-xs text-red-600 mt-1">{fieldError("primary_phone")}</p>}
        </div>
        <div>
          <label className={labelClass}>Secondary phone</label>
          <input className={inputClass} value={secondaryPhone} onChange={(e) => setSecondaryPhone(e.target.value)} />
          {fieldError("secondary_phone") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("secondary_phone")}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Date of birth</label>
        <input type="date" className={inputClass} value={dob || ""} max={new Date().toLocaleDateString("en-CA")} onChange={(e) => setDob(e.target.value)} />
        {fieldError("date_of_birth") && <p className="text-xs text-red-600 mt-1">{fieldError("date_of_birth")}</p>}
      </div>

      <div>
        <label className={labelClass}>Address</label>
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          required
        />
        {fieldError("address") && <p className="text-xs text-red-600 mt-1">{fieldError("address")}</p>}
      </div>

      <div>
        <label className={labelClass}>
          Photo <span className="text-gray-400 font-normal">(optional — leave blank to keep the current photo)</span>
        </label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className={inputClass}
          onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
        />
        {fieldError("photo") && <p className="text-xs text-red-600 mt-1">{fieldError("photo")}</p>}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel} className={`${btnSecondary} flex-1`}>
          Cancel
        </button>
        <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}

// A member type counts as "club" (and therefore needs a club_id) if its
// category description mentions "club" — there's no separate flag on the
// /api/member-types lookup to key off instead. Independent-style types
// (e.g. "Independent Member") don't match and skip the club picker.
const isClubMemberType = (memberType) => /club/i.test(memberType?.description || "");

// Backend fields belonging to the "payment" step of the Add Member wizard —
// everything else belongs to "details". Used to decide, after a submit-time
// 422, whether to jump the user back to "details" so they see a field error
// that isn't visible on "payment" (the step the form was actually submitted
// from).
const MEMBER_PAYMENT_STEP_FIELDS = new Set(["income_id", "amount_paid", "paid_date", "slip_reference", "receipt", "note"]);

// ── Client-side field validators ────────────────────────────────────────
// Mirror the backend's own rules (see the public registration flow's
// equivalents in StudentRegistration.jsx) so a field never bounces between
// "client says ok" and "backend says invalid".
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PHOTO_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
// Sri Lankan phone — local (0XX…) or country-code (+94…/94…) formats.
// Primary must be a mobile: optional 0/94/+94 prefix + 7 + 8 digits.
const isValidPrimaryPhone = (value) => /^(?:\+94|94|0)?7\d{8}$/.test(value.replace(/\s/g, ""));
const isValidSecondaryPhone = (value) => /^(?:\+94|94|0)?\d{9}$/.test(value.replace(/\s/g, ""));
// Single letters each followed by a dot, e.g. "T.N." or "A.B.C.".
const isValidInitials = (value) => /^([A-Za-z]\.\s?)+$/.test(value.trim());
// Old format: 9 digits + V/X. New format: 12 digits.
const isValidNIC = (value) => /^(?:\d{9}[vVxX]|\d{12})$/.test(value.trim());
const isNotFutureDate = (value) => !value || value <= new Date().toISOString().slice(0, 10);
const photoFileError = (file) => {
  if (!file) return null;
  if (!ALLOWED_PHOTO_TYPES.includes(file.type)) return "Photo must be a JPG, PNG, or WebP image.";
  if (file.size > MAX_PHOTO_SIZE_BYTES) return "Photo must be less than 2MB.";
  return null;
};

// ── Member create form ────────────────────────────────────────────────────
// Registers a single member (POST /api/members/legacy). "Coach" is
// deliberately not a selectable member type here — a coach only ever exists
// as part of a club, registered via the Clubs page's "Add Club" form
// instead.
function AddMemberForm({ genders, titles, memberTypes, clubs, incomes, onCancel, onSaved }) {
  const memberTypeOptions = React.useMemo(
    () => memberTypes.filter((t) => !/coach/i.test(t.description || "")),
    [memberTypes]
  );

  const [memberTypeId, setMemberTypeId] = useState("");
  const formRef = React.useRef(null);

  // Registration is a two-step wizard: "details" (personal info) then
  // "payment" (counter-payment record sent together with the member in the
  // same /api/members/legacy request).
  const [step, setStep] = useState("details");

  // ── Payment-step fields (member mode only) ──────────────────────────────
  const [incomeId, setIncomeId] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [slipReference, setSlipReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");

  // ── Member-mode fields ──────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [initials, setInitials] = useState("");
  const [nameDenoted, setNameDenoted] = useState("");
  const [lastname, setLastname] = useState("");
  const [genderId, setGenderId] = useState("");
  const [email, setEmail] = useState("");
  const [nic, setNic] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [clubId, setClubId] = useState("");
  const [membershipStart, setMembershipStart] = useState("");
  const [photoFile, setPhotoFile] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedMemberType = memberTypes.find(
    (t) => String(t.category_id) === String(memberTypeId)
  );
  const needsClub = isClubMemberType(selectedMemberType);

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  // Same look as `inputClass` (see DashboardUI) but with the border/ring
  // colour split out, so a field with a backend error can turn red without
  // fighting `inputClass`'s own gray/blue classes for precedence.
  const fieldClass = (key) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 transition ${
      fieldError(key)
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-gray-200 focus:ring-blue-500 focus:border-blue-500"
    }`;

  // Clears one field's error the moment the user edits it, so a message
  // doesn't sit there stale after the value's already been fixed.
  const clearFieldError = (key) =>
    setFieldErrors((fe) => (fe[key] ? { ...fe, [key]: null } : fe));

  // Client-side validation for the "details" step — same rules the backend
  // enforces, so a field is flagged here before it ever reaches the server.
  const validateDetailsStep = () => {
    const errs = {};
    if (!memberTypeId) errs.member_type_id = ["Please select a member type."];
    if (!title) errs.title = ["Please select a title."];
    if (!genderId) errs.member_gender_id = ["Please select a gender."];
    if (!initials.trim()) errs.initials = ["Please enter the initials."];
    else if (!isValidInitials(initials)) errs.initials = ["Use a dot after each initial, e.g. A.B.C."];
    if (!nameDenoted.trim()) errs.name_denoted_by_initials = ["Please enter the name denoted by initials."];
    if (!lastname.trim()) errs.lastname = ["Please enter the last name."];
    if (!email.trim()) errs.email = ["Please enter an email address."];
    else if (!isValidEmail(email)) errs.email = ["Enter a valid email address."];
    if (!nic.trim()) errs.nic_number = ["Please enter the NIC number."];
    else if (!isValidNIC(nic))
      errs.nic_number = ["Enter a valid NIC — 9 digits + V/X (e.g. 200012345V) or 12 digits."];
    if (!primaryPhone.trim()) errs.primary_phone_number = ["Please enter the primary phone."];
    else if (!isValidPrimaryPhone(primaryPhone))
      errs.primary_phone_number = ["Enter a valid mobile number (e.g. 0712345678 or +94712345678)."];
    if (secondaryPhone && !isValidSecondaryPhone(secondaryPhone))
      errs.secondary_phone_number = ["Enter a valid phone number or leave it empty."];
    if (!dob) errs.date_of_birth = ["Please select a date of birth."];
    else if (!isNotFutureDate(dob)) errs.date_of_birth = ["Date of birth cannot be in the future."];
    if (!address.trim()) errs.address = ["Please enter the address."];
    if (needsClub && !clubId) errs.club_id = ["Please select a club."];
    if (!membershipStart) errs.membership_start_date = ["Please select a membership start date."];
    const photoErr = photoFileError(photoFile);
    if (photoErr) errs.photo = [photoErr];
    return errs;
  };

  // Client-side validation for the "payment" step.
  const validatePaymentStep = () => {
    const errs = {};
    if (!incomeId) errs.income_id = ["Please select an income / fee type."];
    if (!String(amountPaid).trim()) errs.amount_paid = ["Please enter the amount paid."];
    else if (Number.isNaN(Number(amountPaid)) || Number(amountPaid) <= 0)
      errs.amount_paid = ["Enter a valid amount greater than 0."];
    if (!paidDate) errs.paid_date = ["Please select the payment date."];
    else if (!isNotFutureDate(paidDate)) errs.paid_date = ["Payment date cannot be in the future."];
    if (!slipReference.trim()) errs.slip_reference = ["Please enter the slip / reference number."];
    const receiptErr = validateReceiptFile(receiptFile);
    if (receiptErr) errs.receipt = [receiptErr];
    return errs;
  };

  // Advances from the "details" step to the "payment" step — blocked until
  // every details field passes validateDetailsStep().
  const goToPayment = () => {
    const errs = validateDetailsStep();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep("payment");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // A stray Enter keypress in a details-step input submits the form even
    // though the visible action is "Next" — treat that the same as clicking
    // Next instead of firing the real registration request early.
    if (step === "details") {
      goToPayment();
      return;
    }

    const paymentErrs = validatePaymentStep();
    if (Object.keys(paymentErrs).length > 0) {
      setFieldErrors(paymentErrs);
      return;
    }

    setError("");
    setFieldErrors({});
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("initials", initials);
      formData.append("name_denoted_by_initials", nameDenoted);
      formData.append("lastname", lastname);
      formData.append("member_gender_id", genderId);
      formData.append("date_of_birth", dob);
      formData.append("address", address);
      formData.append("email", email);
      formData.append("nic_number", nic);
      formData.append("primary_phone_number", primaryPhone);
      formData.append("secondary_phone_number", secondaryPhone);
      formData.append("member_type_id", memberTypeId);
      if (needsClub) formData.append("club_id", clubId);
      formData.append("membership_start_date", membershipStart);
      if (photoFile) formData.append("photo", photoFile);
      formData.append("income_id", incomeId);
      formData.append("amount_paid", amountPaid);
      formData.append("paid_date", paidDate);
      formData.append("slip_reference", slipReference);
      if (receiptFile) formData.append("receipt", receiptFile);
      if (note) formData.append("note", note);

      const created = await createMemberLegacy(formData);
      toast.success("Member registered.");
      onSaved(created);
    } catch (err) {
      const info = errInfo(err, "Could not register the member.");
      setError(info.message);
      setFieldErrors(info.fields);
      // The form only ever submits from "payment", so any error on a
      // "details" field would otherwise be invisible — jump back to it so
      // the user actually sees the highlighted field and message.
      const hasDetailsError = Object.keys(info.fields).some((k) => !MEMBER_PAYMENT_STEP_FIELDS.has(k));
      if (hasDetailsError) setStep("details");
      setSaving(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      {step === "details" && (
        <div>
          <label className={labelClass}>Member type</label>
          <select
            className={fieldClass("member_type_id")}
            value={memberTypeId}
            onChange={(e) => {
              setMemberTypeId(e.target.value);
              setClubId("");
              clearFieldError("member_type_id");
            }}
            required
          >
            <option value="" disabled>
              Select Member Type
            </option>
            {memberTypeOptions.map((t) => (
              <option key={t.category_id} value={t.category_id}>
                {t.description}
              </option>
            ))}
          </select>
          {fieldError("member_type_id") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("member_type_id")}</p>
          )}
        </div>
      )}

      {step === "details" ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Title</label>
              <select
                className={fieldClass("title")}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  clearFieldError("title");
                }}
                required
              >
                <option value="" disabled>
                  Select Title
                </option>
                {titles.map((t) => (
                  <option key={t.category_id} value={t.description}>
                    {t.description}
                  </option>
                ))}
              </select>
              {fieldError("title") && <p className="text-xs text-red-600 mt-1">{fieldError("title")}</p>}
            </div>
            <div>
              <label className={labelClass}>Gender</label>
              <select
                className={fieldClass("member_gender_id")}
                value={genderId}
                onChange={(e) => {
                  setGenderId(e.target.value);
                  clearFieldError("member_gender_id");
                }}
                required
              >
                <option value="" disabled>
                  Select Gender
                </option>
                {genders.map((g) => (
                  <option key={g.category_id} value={g.category_id}>
                    {g.description}
                  </option>
                ))}
              </select>
              {fieldError("member_gender_id") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("member_gender_id")}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Initials <span className="text-gray-400 font-normal">(e.g. A.B.C.)</span>
              </label>
              <input
                className={fieldClass("initials")}
                value={initials}
                onChange={(e) => {
                  setInitials(e.target.value);
                  clearFieldError("initials");
                }}
                required
              />
              {fieldError("initials") && <p className="text-xs text-red-600 mt-1">{fieldError("initials")}</p>}
            </div>

            <div>
              <label className={labelClass}>Name denoted by initials</label>
              <input
                className={fieldClass("name_denoted_by_initials")}
                value={nameDenoted}
                onChange={(e) => {
                  setNameDenoted(e.target.value);
                  clearFieldError("name_denoted_by_initials");
                }}
                required
              />
              {fieldError("name_denoted_by_initials") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("name_denoted_by_initials")}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Last name</label>
              <input
                className={fieldClass("lastname")}
                value={lastname}
                onChange={(e) => {
                  setLastname(e.target.value);
                  clearFieldError("lastname");
                }}
                required
              />
              {fieldError("lastname") && <p className="text-xs text-red-600 mt-1">{fieldError("lastname")}</p>}
            </div>

            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                className={fieldClass("email")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearFieldError("email");
                }}
                required
              />
              {fieldError("email") && <p className="text-xs text-red-600 mt-1">{fieldError("email")}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>NIC number</label>
              <input
                className={fieldClass("nic_number")}
                value={nic}
                onChange={(e) => {
                  setNic(e.target.value);
                  clearFieldError("nic_number");
                }}
                required
              />
              {fieldError("nic_number") && <p className="text-xs text-red-600 mt-1">{fieldError("nic_number")}</p>}
            </div>

            <div>
              <label className={labelClass}>Date of birth</label>
              <input
                type="date"
                className={fieldClass("date_of_birth")}
                value={dob}
                max={new Date().toLocaleDateString("en-CA")}
                onChange={(e) => {
                  setDob(e.target.value);
                  clearFieldError("date_of_birth");
                }}
                required
              />
              {fieldError("date_of_birth") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("date_of_birth")}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Primary phone</label>
              <input
                className={fieldClass("primary_phone_number")}
                value={primaryPhone}
                onChange={(e) => {
                  setPrimaryPhone(e.target.value);
                  clearFieldError("primary_phone_number");
                }}
                required
              />
              {fieldError("primary_phone_number") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("primary_phone_number")}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Secondary phone</label>
              <input
                className={fieldClass("secondary_phone_number")}
                value={secondaryPhone}
                onChange={(e) => {
                  setSecondaryPhone(e.target.value);
                  clearFieldError("secondary_phone_number");
                }}
              />
              {fieldError("secondary_phone_number") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("secondary_phone_number")}</p>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>Address</label>
            <textarea
              className={`${fieldClass("address")} resize-none`}
              rows={2}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                clearFieldError("address");
              }}
              required
            />
            {fieldError("address") && <p className="text-xs text-red-600 mt-1">{fieldError("address")}</p>}
          </div>

          {needsClub && (
            <div>
              <label className={labelClass}>Club</label>
              <select
                className={fieldClass("club_id")}
                value={clubId}
                onChange={(e) => {
                  setClubId(e.target.value);
                  clearFieldError("club_id");
                }}
                required
              >
                <option value="" disabled>
                  Select a club…
                </option>
                {clubs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.club_name}
                  </option>
                ))}
              </select>
              {fieldError("club_id") && <p className="text-xs text-red-600 mt-1">{fieldError("club_id")}</p>}
            </div>
          )}

          <div>
            <label className={labelClass}>Membership start date</label>
            <input
              type="date"
              className={fieldClass("membership_start_date")}
              value={membershipStart}
              onChange={(e) => {
                setMembershipStart(e.target.value);
                clearFieldError("membership_start_date");
              }}
              required
            />
            {fieldError("membership_start_date") && (
              <p className="text-xs text-red-600 mt-1">{fieldError("membership_start_date")}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>
              Photo <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className={fieldClass("photo")}
              onChange={(e) => {
                setPhotoFile(e.target.files?.[0] || null);
                clearFieldError("photo");
              }}
            />
            {fieldError("photo") && <p className="text-xs text-red-600 mt-1">{fieldError("photo")}</p>}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Income / fee type</label>
              <select
                className={fieldClass("income_id")}
                value={incomeId}
                onChange={(e) => {
                  const id = e.target.value;
                  setIncomeId(id);
                  clearFieldError("income_id");
                }}
                required
              >
                <option value="" disabled>
                  Select…
                </option>
                {incomes.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.description}
                  </option>
                ))}
              </select>
              {fieldError("income_id") && <p className="text-xs text-red-600 mt-1">{fieldError("income_id")}</p>}
            </div>

            <div>
              <label className={labelClass}>Slip / reference number</label>
              <input
                className={fieldClass("slip_reference")}
                value={slipReference}
                onChange={(e) => {
                  setSlipReference(e.target.value);
                  clearFieldError("slip_reference");
                }}
                required
              />
              {fieldError("slip_reference") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("slip_reference")}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Amount paid</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={fieldClass("amount_paid")}
                value={amountPaid}
                onChange={(e) => {
                  setAmountPaid(e.target.value);
                  clearFieldError("amount_paid");
                }}
                required
              />
              {fieldError("amount_paid") && <p className="text-xs text-red-600 mt-1">{fieldError("amount_paid")}</p>}
            </div>
            <div>
              <label className={labelClass}>Payment date</label>
              <input
                type="date"
                className={fieldClass("paid_date")}
                value={paidDate}
                onChange={(e) => {
                  setPaidDate(e.target.value);
                  clearFieldError("paid_date");
                }}
                required
              />
              {fieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_date")}</p>}
            </div>
          </div>

          <ReceiptFileInput
            file={receiptFile}
            onChange={(f) => {
              setReceiptFile(f);
              clearFieldError("receipt");
            }}
            error={fieldError("receipt")}
          />

          <div>
            <label className={labelClass}>
              Note <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              className={`${fieldClass("note")} resize-none`}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            {fieldError("note") && <p className="text-xs text-red-600 mt-1">{fieldError("note")}</p>}
          </div>
        </>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-1">
        {step === "payment" ? (
          <>
            <button
              type="button"
              onClick={() => setStep("details")}
              disabled={saving}
              className={`${btnSecondary} flex-1`}
            >
              Back
            </button>
            <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
              {saving ? "Registering…" : "Submit"}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onCancel} className={`${btnSecondary} flex-1`}>
              Cancel
            </button>
            <button type="button" onClick={goToPayment} className={`${btnPrimary} flex-1`}>
              Next
            </button>
          </>
        )}
      </div>
    </form>
  );
}

// ── Staff-desk ("Counter Registration") form ────────────────────────────────
// Registers a single member via POST /api/staff/member-registrations
// (createStaffMemberRegistration) — a PENDING process the walk-in member
// pays for at the counter afterwards, unlike AddMemberForm above (legacy
// /api/members/legacy, which finalizes immediately together with a payment
// record entered on the same form). Field names/validation mirror the
// public registration flow (StudentRegistration.jsx) rather than the legacy
// form: primary_phone/secondary_phone/nic (not *_number), and
// membership_type "independent"|"club" (not a member_type_id lookup).
function StaffMemberRegistrationForm({ genders, titles, clubs, onDownloadVoucher, downloadingVoucherId, onDone }) {
  const [title, setTitle] = useState("");
  const [initials, setInitials] = useState("");
  const [nameDenoted, setNameDenoted] = useState("");
  const [lastname, setLastname] = useState("");
  const [genderId, setGenderId] = useState("");
  const [email, setEmail] = useState("");
  const [nic, setNic] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [membershipType, setMembershipType] = useState("independent");
  const [clubId, setClubId] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  // Object URL for the selected photo, shown on the review step so staff can
  // catch a wrong file before submitting — created once on selection,
  // revoked on replace/reset/unmount (same lifecycle as ClubRegistration.jsx's
  // own coach photo previews).
  const [photoPreview, setPhotoPreview] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  // "details" (data entry) → "summary" (read-only review before the actual
  // POST) — mirrors the public registration flow's own details→summary step.
  const [step, setStep] = useState("details");
  // Response `data` from POST /api/staff/member-registrations/preview — the
  // backend-validated (normalized) fields plus payable_amount, rendered on the
  // "summary" step. Only set by a successful preview; cleared on Back.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Set once a pending process has been created — swaps the form out for a
  // "what's next" panel (print voucher, then reconcile via the existing
  // Confirm Payment tab once the member actually pays at the counter).
  const [result, setResult] = useState(null);

  useEffect(() => () => photoPreview && URL.revokeObjectURL(photoPreview), [photoPreview]);

  const genderName = (id) => genders.find((g) => String(g.category_id) === String(id))?.description || "—";
  const clubName = (id) => clubs.find((c) => String(c.id) === String(id))?.club_name || "—";

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };
  const fieldClass = (key) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 transition ${
      fieldError(key)
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-gray-200 focus:ring-blue-500 focus:border-blue-500"
    }`;
  const clearFieldError = (key) =>
    setFieldErrors((fe) => (fe[key] ? { ...fe, [key]: null } : fe));

  const validate = () => {
    const errs = {};
    if (!title) errs.title = ["Please select a title."];
    if (!genderId) errs.member_gender_id = ["Please select a gender."];
    if (!initials.trim()) errs.initials = ["Please enter the initials."];
    else if (!isValidInitials(initials)) errs.initials = ["Use a dot after each initial, e.g. A.B.C."];
    if (!nameDenoted.trim()) errs.name_denoted_by_initials = ["Please enter the name denoted by initials."];
    if (!lastname.trim()) errs.lastname = ["Please enter the last name."];
    if (!email.trim()) errs.email = ["Please enter an email address."];
    else if (!isValidEmail(email)) errs.email = ["Enter a valid email address."];
    if (!nic.trim()) errs.nic = ["Please enter the NIC number."];
    else if (!isValidNIC(nic)) errs.nic = ["Enter a valid NIC — 9 digits + V/X (e.g. 200012345V) or 12 digits."];
    if (!primaryPhone.trim()) errs.primary_phone = ["Please enter the primary phone."];
    else if (!isValidPrimaryPhone(primaryPhone))
      errs.primary_phone = ["Enter a valid mobile number (e.g. 0712345678 or +94712345678)."];
    if (secondaryPhone && !isValidSecondaryPhone(secondaryPhone))
      errs.secondary_phone = ["Enter a valid phone number or leave it empty."];
    if (dob && !isNotFutureDate(dob)) errs.date_of_birth = ["Date of birth cannot be in the future."];
    if (!address.trim()) errs.address = ["Please enter the address."];
    if (membershipType === "club" && !clubId) errs.club_id = ["Please select a club."];
    const photoErr = photoFileError(photoFile);
    if (photoErr) errs.photo = [photoErr];
    else if (!photoFile) errs.photo = ["A profile photo is required."];
    return errs;
  };

  const reset = () => {
    setTitle("");
    setInitials("");
    setNameDenoted("");
    setLastname("");
    setGenderId("");
    setEmail("");
    setNic("");
    setPrimaryPhone("");
    setSecondaryPhone("");
    setDob("");
    setAddress("");
    setMembershipType("independent");
    setClubId("");
    setPhotoFile(null);
    setPhotoPreview(null);
    setFieldErrors({});
    setError("");
    setStep("details");
    setPreview(null);
    setResult(null);
  };

  // Same multipart payload for both the preview and the real create call.
  const buildFormData = () => {
    const fd = new FormData();
    fd.append("title", title);
    fd.append("initials", initials);
    fd.append("name_denoted_by_initials", nameDenoted);
    fd.append("lastname", lastname);
    fd.append("member_gender_id", genderId);
    fd.append("email", email);
    fd.append("nic", nic);
    fd.append("primary_phone", primaryPhone);
    if (secondaryPhone) fd.append("secondary_phone", secondaryPhone);
    if (dob) fd.append("date_of_birth", dob);
    fd.append("address", address);
    fd.append("membership_type", membershipType);
    if (membershipType === "club") fd.append("club_id", clubId);
    fd.append("photo", photoFile);
    return fd;
  };

  // Client-side checks first (instant feedback), then the backend dry run —
  // it re-runs the real create route's validation (incl. email uniqueness)
  // and resolves the fee, without writing anything.
  const goToSummary = async () => {
    if (previewing) return;
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setError("");
    setPreviewing(true);
    try {
      const data = await previewStaffMemberRegistration(buildFormData());
      setPreview(data);
      setStep("summary");
    } catch (err) {
      const info = errInfo(err, "Could not validate this registration.");
      setError(info.message);
      setFieldErrors(info.fields);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // A stray Enter keypress on "details" submits the form even though the
    // visible action is "Review", so treat that the same as clicking it
    // instead of firing the real request a step early.
    if (step === "details") {
      goToSummary();
      return;
    }

    setError("");
    setSaving(true);
    try {
      const created = await createStaffMemberRegistration(buildFormData());
      setResult(created);
      setSaving(false);
    } catch (err) {
      const info = errInfo(err, "Could not create this registration.");
      setError(info.message);
      setFieldErrors(info.fields);
      // The form only ever submits from "summary" — jump back to "details" so
      // a field error is actually visible next to its input.
      if (Object.keys(info.fields).length > 0) {
        setStep("details");
        setPreview(null);
      }
      setSaving(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-xl mx-auto text-center py-4">
        <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
          <Icon path={ICONS.check} className="w-7 h-7 text-green-600" width={2} />
        </div>
        <h3 className="text-lg font-bold mb-1" style={{ color: TOKENS.NAVY }}>
          Registration process created
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          Print the voucher below and hand it to the member. Once they pay at the counter, reconcile it from the
          "Confirm Payment" tab using this reference.
        </p>
        <div className="bg-gray-50 rounded-xl p-4 mb-5 text-left space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Payment reference</span>
            <span className="font-mono font-semibold text-gray-800">{result.payment_reference || "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Payable amount</span>
            <span className="font-semibold text-gray-800">{money(result.payable_amount)}</span>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onDownloadVoucher(result.process_id)}
            disabled={downloadingVoucherId === result.process_id}
            className={`${btnPrimary} flex-1`}
          >
            {downloadingVoucherId === result.process_id ? "Preparing…" : "Download Voucher"}
          </button>
          <button onClick={reset} className={`${btnSecondary} flex-1`}>
            Register Another
          </button>
        </div>
        <button onClick={onDone} className="mt-4 text-sm font-semibold" style={{ color: TOKENS.PRIMARY }}>
          Done
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {step === "details" && (
        <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Title</label>
          <select
            className={fieldClass("title")}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              clearFieldError("title");
            }}
            required
          >
            <option value="" disabled>
              Select Title
            </option>
            {titles.map((t) => (
              <option key={t.category_id} value={t.description}>
                {t.description}
              </option>
            ))}
          </select>
          {fieldError("title") && <p className="text-xs text-red-600 mt-1">{fieldError("title")}</p>}
        </div>
        <div>
          <label className={labelClass}>Gender</label>
          <select
            className={fieldClass("member_gender_id")}
            value={genderId}
            onChange={(e) => {
              setGenderId(e.target.value);
              clearFieldError("member_gender_id");
            }}
            required
          >
            <option value="" disabled>
              Select Gender
            </option>
            {genders.map((g) => (
              <option key={g.category_id} value={g.category_id}>
                {g.description}
              </option>
            ))}
          </select>
          {fieldError("member_gender_id") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("member_gender_id")}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            Initials <span className="text-gray-400 font-normal">(e.g. A.B.C.)</span>
          </label>
          <input
            className={fieldClass("initials")}
            value={initials}
            onChange={(e) => {
              setInitials(e.target.value);
              clearFieldError("initials");
            }}
            required
          />
          {fieldError("initials") && <p className="text-xs text-red-600 mt-1">{fieldError("initials")}</p>}
        </div>
        <div>
          <label className={labelClass}>Name denoted by initials</label>
          <input
            className={fieldClass("name_denoted_by_initials")}
            value={nameDenoted}
            onChange={(e) => {
              setNameDenoted(e.target.value);
              clearFieldError("name_denoted_by_initials");
            }}
            required
          />
          {fieldError("name_denoted_by_initials") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("name_denoted_by_initials")}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Last name</label>
          <input
            className={fieldClass("lastname")}
            value={lastname}
            onChange={(e) => {
              setLastname(e.target.value);
              clearFieldError("lastname");
            }}
            required
          />
          {fieldError("lastname") && <p className="text-xs text-red-600 mt-1">{fieldError("lastname")}</p>}
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input
            type="email"
            className={fieldClass("email")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            required
          />
          {fieldError("email") && <p className="text-xs text-red-600 mt-1">{fieldError("email")}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>NIC number</label>
          <input
            className={fieldClass("nic")}
            value={nic}
            onChange={(e) => {
              setNic(e.target.value);
              clearFieldError("nic");
            }}
            required
          />
          {fieldError("nic") && <p className="text-xs text-red-600 mt-1">{fieldError("nic")}</p>}
        </div>
        <div>
          <label className={labelClass}>
            Date of birth <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="date"
            className={fieldClass("date_of_birth")}
            value={dob}
            max={new Date().toLocaleDateString("en-CA")}
            onChange={(e) => {
              setDob(e.target.value);
              clearFieldError("date_of_birth");
            }}
          />
          {fieldError("date_of_birth") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("date_of_birth")}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Primary phone</label>
          <input
            className={fieldClass("primary_phone")}
            value={primaryPhone}
            onChange={(e) => {
              setPrimaryPhone(e.target.value);
              clearFieldError("primary_phone");
            }}
            required
          />
          {fieldError("primary_phone") && <p className="text-xs text-red-600 mt-1">{fieldError("primary_phone")}</p>}
        </div>
        <div>
          <label className={labelClass}>Secondary phone</label>
          <input
            className={fieldClass("secondary_phone")}
            value={secondaryPhone}
            onChange={(e) => {
              setSecondaryPhone(e.target.value);
              clearFieldError("secondary_phone");
            }}
          />
          {fieldError("secondary_phone") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("secondary_phone")}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Address</label>
        <textarea
          className={`${fieldClass("address")} resize-none`}
          rows={2}
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            clearFieldError("address");
          }}
          required
        />
        {fieldError("address") && <p className="text-xs text-red-600 mt-1">{fieldError("address")}</p>}
      </div>

      <div>
        <label className={labelClass}>Membership type</label>
        <div className="flex gap-4">
          {[
            ["independent", "Independent"],
            ["club", "Club"],
          ].map(([value, label]) => (
            <label key={value} className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="membershipType"
                checked={membershipType === value}
                onChange={() => {
                  setMembershipType(value);
                  if (value !== "club") setClubId("");
                  clearFieldError("membership_type");
                  clearFieldError("club_id");
                }}
              />
              {label}
            </label>
          ))}
        </div>
        {fieldError("membership_type") && (
          <p className="text-xs text-red-600 mt-1">{fieldError("membership_type")}</p>
        )}
      </div>

      {membershipType === "club" && (
        <div>
          <label className={labelClass}>Club</label>
          <select
            className={fieldClass("club_id")}
            value={clubId}
            onChange={(e) => {
              setClubId(e.target.value);
              clearFieldError("club_id");
            }}
            required
          >
            <option value="" disabled>
              Select a club…
            </option>
            {clubs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.club_name}
              </option>
            ))}
          </select>
          {fieldError("club_id") && <p className="text-xs text-red-600 mt-1">{fieldError("club_id")}</p>}
        </div>
      )}

      <div>
        <label className={labelClass}>Photo</label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className={fieldClass("photo")}
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            setPhotoFile(f);
            setPhotoPreview(f ? URL.createObjectURL(f) : null);
            clearFieldError("photo");
          }}
          required
        />
        {fieldError("photo") && <p className="text-xs text-red-600 mt-1">{fieldError("photo")}</p>}
      </div>
        </>
      )}

      {step === "summary" && preview && (
        <div className="rounded-xl border border-gray-100 p-4 space-y-1.5">
          <div className="flex items-center gap-3 mb-3">
            <Avatar
              name={[preview.title, preview.initials, preview.lastname].filter(Boolean).join(" ")}
              url={photoPreview}
              size={48}
            />
            <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
              Review before submitting
            </p>
          </div>
          {/* Values as validated/normalized by the backend preview, not the raw inputs. */}
          {[
            [
              "Name",
              [preview.title, preview.initials, preview.name_denoted_by_initials, preview.lastname]
                .filter(Boolean)
                .join(" ") || "—",
            ],
            ["Gender", genderName(preview.member_gender_id)],
            ["Email", preview.email || "—"],
            ["NIC", preview.nic || "—"],
            ["Primary phone", preview.primary_phone || "—"],
            ["Secondary phone", preview.secondary_phone || "—"],
            ["Date of birth", preview.date_of_birth || "—"],
            ["Address", preview.address || "—"],
            ["Membership type", preview.membership_type === "independent" ? "Independent" : "Club"],
            ...(preview.membership_type !== "independent" ? [["Club", clubName(preview.club_id)]] : []),
            ["Fee", preview.income_description || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 border-b border-gray-50 last:border-0 text-sm">
              <span className="text-gray-500">{k}</span>
              <span className="font-medium text-gray-800 text-right max-w-[60%] truncate">{v}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 text-sm">
            <span className="font-semibold" style={{ color: TOKENS.NAVY }}>
              Payable amount
            </span>
            <span className="font-bold" style={{ color: TOKENS.NAVY }}>
              {money(preview.payable_amount)}
            </span>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-1">
        {step === "summary" ? (
          <>
            <button
              type="button"
              onClick={() => {
                // Any edit on "details" invalidates the preview — it is
                // re-fetched on the next Review.
                setStep("details");
                setPreview(null);
              }}
              disabled={saving}
              className={`${btnSecondary} flex-1`}
            >
              Back
            </button>
            <button key="confirm" type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
              {saving ? "Creating…" : "Confirm & Register"}
            </button>
          </>
        ) : (
          <button
            key="review"
            type="button"
            onClick={goToSummary}
            disabled={previewing}
            className={`${btnPrimary} flex-1`}
          >
            {previewing ? "Checking…" : "Review"}
          </button>
        )}
      </div>
    </form>
  );
}

// ── List page ─────────────────────────────────────────────────────────────
function MembersManagementList() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  // Instructor-only UI restriction (frontend-only — no permission change):
  // hides the "Download Voucher" link on the "Pending Registrations" sub-tab
  // for the Instructor role. Case-insensitive against user.roles (slugs),
  // same convention isSuperAdmin uses in AuthContext.
  const isInstructor = (user?.roles || []).some((r) => String(r).toLowerCase() === "instructor");
  const canViewDetails = can("view_member_details");
  // Gates the "Pending Registrations" sub-tab specifically — a separate
  // permission from "view_member_details" above (which gates the per-member
  // "View" action/detail page instead). Not seeded onto any role but Super
  // Admin/Director today (see SFMIS-Backend's SuperAdminRoleSeeder) — the
  // backend route itself only checks system.user, not this permission, so
  // this is a frontend-only visibility gate, stricter than the backend
  // requires (same as the note on view_member_details doing the same job
  // pre-this-change).
  const canViewMemberRegistrations = can("view_member_registrations");
  const canEdit = can("edit_members");
  // Gates the "Add Member" sub-tab, which registers a member via the legacy
  // admin-created flow (POST /api/members/legacy — see createMemberLegacy).
  const canCreate = can("create_legacy_members");
  // Gates the "Counter Registration" sub-tab — staff-desk registration via
  // POST /api/staff/member-registrations (createStaffMemberRegistration), a
  // separate permission/flow from create_legacy_members above: this one
  // creates a PENDING process paid later at the counter (voucher + the
  // existing Confirm Payment tab), not a one-step finalize-with-payment.
  const canCreateStaffRegistration = can("create_staff_member_registrations");
  // Gates the "Confirm Payment" sub-tab (manual/shroff registration-payment
  // reconciliation). Search needs "view_payments"; submitting a receipt
  // additionally needs "submit_manual_payment_receipts" (the Instructor role
  // has this without having broader payment permissions).
  const canViewPayments = can("view_payments");
  const canConfirmPayments = can("submit_manual_payment_receipts");
  // The "Confirm Payment" tab is also shown to holders of
  // "confirm_shroff_payments" (e.g. the Shroff role) — the backend's
  // */manual-confirm routes accept either permission.
  const canConfirmShroff = can("confirm_shroff_payments");
  const canSeeConfirmTab = canViewPayments || canConfirmShroff;
  const canSubmitConfirm = canConfirmPayments || canConfirmShroff;
  // Gates the "Deactivate"/"Reactivate" row actions — separate permission
  // from edit_members, seeded onto admin/Director Physical Education/
  // Instructor (see SFMIS-Backend's AddMemberInactiveStatusAndDeactivate
  // PermissionsSeeder).
  const canDeactivate = can("deactivate_members");
  // Gates the "Change Club" row action (Independent ⇄ Club Student via
  // /api/members/{id}/club-request). Not seeded onto any role by default.
  const canChangeClub = can("request_member_club_change");

  const [members, setMembers] = useState([]);
  const [memberTypes, setMemberTypes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [titles, setTitles] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // modal: { type: "edit", member } — "view" now navigates to its own page
  // (MemberDetailPage); "create" lives in its own in-page tab (see
  // activeTab below) instead of a modal.
  const [modal, setModal] = useState(null);

  // "list" (browse/search table), "add" (registration form), "pending"
  // (registration processes — see canViewMemberRegistrations gate below),
  // "confirm-payment" (manual/shroff payment reconciliation — see
  // canViewPayments gate below), or "my-submissions" (this instructor's own
  // submitted receipts — see canConfirmPayments gate below) — each button
  // switches this tab instead of opening a popup / navigating away.
  const [activeTab, setActiveTab] = useState("list");

  const [pendingProcesses, setPendingProcesses] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingSearch, setPendingSearch] = useState("");
  const [downloadingVoucherId, setDownloadingVoucherId] = useState(null);
  const [reactivatingId, setReactivatingId] = useState(null);

  // Members whose status is Expired or whose membership_end_date has already
  // passed (GET /api/members/expired) — a fixed-eligibility view, no
  // date-range picker, matching the backend's own "as of today" definition.
  const [expiredMembers, setExpiredMembers] = useState([]);
  const [expiredAsOf, setExpiredAsOf] = useState(null);
  const [expiredLoading, setExpiredLoading] = useState(true);
  const [expiredSearch, setExpiredSearch] = useState("");

  // Open a blank tab synchronously (inside the click handler) so the browser
  // doesn't treat it as a popup — navigating it to the blob URL after the
  // `await` below would otherwise get blocked in most browsers.
  const handleDownloadVoucher = async (processId) => {
    if (downloadingVoucherId) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucherId(processId);
    try {
      const blob = await getMemberShroffVoucherPdf(processId);
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      tab?.close();
      toast.error(errInfo(err, "Could not download the voucher.").message);
    } finally {
      setDownloadingVoucherId(null);
    }
  };

  const handleReactivate = async (member) => {
    if (reactivatingId) return;
    if (!window.confirm(`Reactivate ${fullName(member)}? Their account will be marked Active again.`)) return;
    setReactivatingId(member.id);
    try {
      const r = await reactivateMember(member.id);
      toast.success(r?.message || "Member reactivated.");
      fetchMembers();
    } catch (err) {
      toast.error(errInfo(err, "Could not reactivate this member.").message);
    } finally {
      setReactivatingId(null);
    }
  };

  const fetchMembers = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (typeFilter) params.member_type_id = typeFilter;
    listMembers(params)
      .then(setMembers)
      .catch((err) => toast.error(errInfo(err, "Could not load members.").message))
      .finally(() => setLoading(false));
  }, [search, typeFilter]);

  useEffect(() => {
    const t = setTimeout(fetchMembers, 300);
    return () => clearTimeout(t);
  }, [fetchMembers]);

  // Every member registration attempt (pending, paid-but-unfinalized,
  // completed) — not just the finalized rows listMembers() returns. Only
  // fetched while the tab is actually open.
  const fetchPendingProcesses = useCallback(() => {
    setPendingLoading(true);
    const params = {};
    if (pendingSearch.trim()) params.search = pendingSearch.trim();
    listMemberRegistrationProcesses(params)
      .then(setPendingProcesses)
      .catch((err) => toast.error(errInfo(err, "Could not load member registration processes.").message))
      .finally(() => setPendingLoading(false));
  }, [pendingSearch]);

  useEffect(() => {
    if (activeTab !== "pending") return;
    const t = setTimeout(fetchPendingProcesses, 300);
    return () => clearTimeout(t);
  }, [activeTab, fetchPendingProcesses]);

  // Only fetched while the "Expired Members" tab is actually open, same
  // lazy-load pattern as fetchPendingProcesses above.
  const fetchExpiredMembers = useCallback(() => {
    setExpiredLoading(true);
    const params = {};
    if (expiredSearch.trim()) params.search = expiredSearch.trim();
    listExpiredMembers(params)
      .then(({ as_of, members }) => {
        setExpiredAsOf(as_of);
        setExpiredMembers(members);
      })
      .catch((err) => toast.error(errInfo(err, "Could not load expired members.").message))
      .finally(() => setExpiredLoading(false));
  }, [expiredSearch]);

  useEffect(() => {
    if (activeTab !== "expired") return;
    const t = setTimeout(fetchExpiredMembers, 300);
    return () => clearTimeout(t);
  }, [activeTab, fetchExpiredMembers]);

  useEffect(() => {
    listMemberTypes().then(setMemberTypes).catch(() => {});
    listMemberGenders().then(setGenders).catch(() => {});
    listMemberTitles().then(setTitles).catch(() => {});
    listClubs().then(setClubs).catch(() => {});
    listFees().then(setIncomes).catch(() => {});
  }, []);

  const onSaved = () => {
    setModal(null);
    setActiveTab("list");
    fetchMembers();
  };

  // Each sub-tab's list is fetched in full (search/filter narrows it
  // server-side, but there's no page/per_page support on any of these three
  // endpoints) — paginate client-side. resetKey includes activeTab so
  // switching tabs always lands back on page 1 of that tab's list.
  const membersPagination = usePagination(members, { pageSize: DEFAULT_PAGE_SIZE, resetKey: `${activeTab}|${search}|${typeFilter}` });
  const pendingPagination = usePagination(pendingProcesses, { pageSize: DEFAULT_PAGE_SIZE, resetKey: `${activeTab}|${pendingSearch}` });
  const expiredPagination = usePagination(expiredMembers, { pageSize: DEFAULT_PAGE_SIZE, resetKey: `${activeTab}|${expiredSearch}` });

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Members
        </h1>
        <p className="text-sm text-gray-500">Browse and update registered member records.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("list")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === "list" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          style={activeTab === "list" ? { color: TOKENS.PRIMARY } : undefined}
        >
          All Members
        </button>
        {canCreate && (
          <button
            onClick={() => setActiveTab("add")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "add" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "add" ? { color: TOKENS.PRIMARY } : undefined}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Add Member
          </button>
        )}
        {canCreateStaffRegistration && (
          <button
            onClick={() => setActiveTab("staff-register")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "staff-register" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "staff-register" ? { color: TOKENS.PRIMARY } : undefined}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Counter Registration
          </button>
        )}
        {canViewMemberRegistrations && (
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "pending" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "pending" ? { color: TOKENS.PRIMARY } : undefined}
          >
            Pending Registrations
          </button>
        )}
        <button
          onClick={() => setActiveTab("expired")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === "expired" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          style={activeTab === "expired" ? { color: TOKENS.PRIMARY } : undefined}
        >
          Expired Members
        </button>
        {canSeeConfirmTab && (
          <button
            onClick={() => setActiveTab("confirm-payment")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "confirm-payment" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "confirm-payment" ? { color: TOKENS.PRIMARY } : undefined}
          >
            Confirm Payment
          </button>
        )}
        {canConfirmPayments && (
          <button
            onClick={() => setActiveTab("my-submissions")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "my-submissions" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "my-submissions" ? { color: TOKENS.PRIMARY } : undefined}
          >
            My Submissions
          </button>
        )}
      </div>

      {activeTab === "my-submissions" && canConfirmPayments ? (
        <MySubmissionsTab />
      ) : activeTab === "confirm-payment" && canSeeConfirmTab ? (
        <ConfirmShroffPaymentTab canConfirm={canSubmitConfirm} />
      ) : activeTab === "expired" ? (
        <div>
          {/* Search */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder="Search by name, email, NIC or club…"
                value={expiredSearch}
                onChange={(e) => setExpiredSearch(e.target.value)}
              />
            </div>
            {expiredAsOf && <p className="text-xs text-gray-400 whitespace-nowrap">As of {expiredAsOf}</p>}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            {expiredLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner />
                <p className="text-sm text-gray-400">Loading expired members…</p>
              </div>
            ) : expiredMembers.length === 0 ? (
              <EmptyState icon={ICONS.user} text="No expired members found." />
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full text-left min-w-[860px]">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-3 pr-3 font-medium">Member</th>
                      <th className="py-3 px-3 font-medium">Type</th>
                      <th className="py-3 px-3 font-medium">Club</th>
                      <th className="py-3 px-3 font-medium">Status</th>
                      <th className="py-3 px-3 font-medium">Expired On</th>
                      <th className="py-3 pl-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiredPagination.pageItems.map((m) => (
                      <tr key={m.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-4 pr-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={fullName(m)} url={m.photo_url} size={42} />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                                {fullName(m)}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{m.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-3">
                          <span className="inline-block px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold whitespace-nowrap">
                            {m.member_type}
                          </span>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-600 whitespace-nowrap">{m.club_name || "—"}</td>
                        <td className="py-4 px-3">
                          <Pill>{m.member_status}</Pill>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-600 whitespace-nowrap">{m.membership_end_date || "—"}</td>
                        <td className="py-4 pl-3">
                          <div className="flex items-center justify-end gap-3">
                            {canViewDetails && (
                              <button
                                onClick={() => navigate(`${m.id}`)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                              >
                                <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                                View
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {expiredPagination.totalPages > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Pagination page={expiredPagination.page} totalPages={expiredPagination.totalPages} onPageChange={expiredPagination.setPage} />
              </div>
            )}
          </div>
        </div>
      ) : activeTab === "pending" && canViewMemberRegistrations ? (
        <div>
          {/* Search */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder="Search by name, email, NIC, phone or club…"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            {pendingLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner />
                <p className="text-sm text-gray-400">Loading member registration processes…</p>
              </div>
            ) : pendingProcesses.length === 0 ? (
              <EmptyState icon={ICONS.userCheck} text="No member registration processes found." />
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full text-left min-w-[960px]">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-3 pr-3 font-medium">Applicant</th>
                      <th className="py-3 px-3 font-medium">Type</th>
                      <th className="py-3 px-3 font-medium">Club</th>
                      <th className="py-3 px-3 font-medium">Payment</th>
                      <th className="py-3 px-3 font-medium">Process</th>
                      <th className="py-3 px-3 font-medium">Paid</th>
                      <th className="py-3 px-3 font-medium">Date</th>
                      <th className="py-3 pl-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPagination.pageItems.map((p) => (
                      <tr key={p.process_id} className="border-b border-gray-50 last:border-0">
                        <td className="py-4 pr-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={p.full_name} url={storageUrl(p.photo_path)} size={42} />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                                {p.full_name}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{p.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-3">
                          <span className="inline-block px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold whitespace-nowrap">
                            {p.member_type || "—"}
                          </span>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-600 whitespace-nowrap">
                          {p.club_name || p.requested_club_name || "—"}
                        </td>
                        <td className="py-4 px-3">
                          <Pill>{p.payment_status}</Pill>
                        </td>
                        <td className="py-4 px-3">
                          <Pill>{p.process_status}</Pill>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-600 whitespace-nowrap">{money(p.paid_amount)}</td>
                        <td className="py-4 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(p.created_at)}</td>
                        <td className="py-4 pl-3">
                          <div className="flex items-center justify-end gap-3">
                            {!isInstructor && (
                              <button
                                onClick={() => handleDownloadVoucher(p.process_id)}
                                disabled={downloadingVoucherId === p.process_id}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-50"
                              >
                                <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                                {downloadingVoucherId === p.process_id ? "Preparing…" : "Download Voucher"}
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`pending-registrations/${p.process_id}`)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                            >
                              <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {pendingPagination.totalPages > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Pagination page={pendingPagination.page} totalPages={pendingPagination.totalPages} onPageChange={pendingPagination.setPage} />
              </div>
            )}
          </div>
        </div>
      ) : activeTab === "add" && canCreate ? (
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-7 max-w-full">
          <AddMemberForm
            genders={genders}
            titles={titles}
            memberTypes={memberTypes}
            clubs={clubs}
            incomes={incomes}
            onCancel={() => setActiveTab("list")}
            onSaved={onSaved}
          />
        </div>
      ) : activeTab === "staff-register" && canCreateStaffRegistration ? (
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-7 max-w-full">
          <StaffMemberRegistrationForm
            genders={genders}
            clubs={clubs}
            titles={titles}
            onDownloadVoucher={handleDownloadVoucher}
            downloadingVoucherId={downloadingVoucherId}
            onDone={() => setActiveTab(canViewMemberRegistrations ? "pending" : "list")}
          />
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder="Search by name, email or NIC…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className={`${inputClass} w-auto`}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              {memberTypes.map((t) => (
                <option key={t.category_id} value={t.category_id}>
                  {t.description}
                </option>
              ))}
            </select>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner />
                <p className="text-sm text-gray-400">Loading members…</p>
              </div>
            ) : members.length === 0 ? (
              <EmptyState icon={ICONS.user} text="No members found." />
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full text-left min-w-[860px]">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-3 pr-3 font-medium">Member</th>
                      <th className="py-3 px-3 font-medium">Type</th>
                      <th className="py-3 px-3 font-medium">Club</th>
                      <th className="py-3 px-3 font-medium">Status</th>
                      <th className="py-3 px-3 font-medium">Member Code</th>
                      <th className="py-3 pl-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersPagination.pageItems.map((m) => (
                      <tr key={m.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-4 pr-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={fullName(m)} url={m.photo_url} size={42} />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                                {fullName(m)}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{m.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-3">
                          <span className="inline-block px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold whitespace-nowrap">
                            {m.member_type}
                          </span>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-600 whitespace-nowrap">{m.club_name || "—"}</td>
                        <td className="py-4 px-3">
                          <Pill>{m.member_status}</Pill>
                        </td>
                        <td className="py-4 px-3 text-sm font-mono text-gray-600 whitespace-nowrap">{m.member_code || "—"}</td>
                        <td className="py-4 pl-3">
                          <div className="flex items-center justify-end gap-3">
                            {canViewDetails && (
                              <button
                                onClick={() => navigate(`${m.id}`)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                              >
                                <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                                View
                              </button>
                            )}
                            {canEdit && (
                              <button
                                onClick={() => setModal({ type: "edit", member: m })}
                                className="inline-flex items-center gap-1 text-xs font-semibold"
                                style={{ color: TOKENS.PRIMARY }}
                              >
                                <Icon path={ICONS.edit} className="w-4 h-4" width={1.8} />
                                Edit
                              </button>
                            )}
                            {canChangeClub && m.member_type !== "Coach" && (
                              <button
                                onClick={() => setModal({ type: "change-club", member: m })}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                              >
                                <Icon path={ICONS.refresh} className="w-4 h-4" width={1.8} />
                                Change Club
                              </button>
                            )}
                            {canDeactivate && m.member_status === "Active" && (
                              <button
                                onClick={() => setModal({ type: "deactivate", member: m })}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700"
                              >
                                <Icon path={ICONS.x} className="w-4 h-4" width={1.8} />
                                Deactivate
                              </button>
                            )}
                            {canDeactivate && m.member_status === "Inactive" && (
                              <button
                                onClick={() => handleReactivate(m)}
                                disabled={reactivatingId === m.id}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 hover:text-green-800 disabled:opacity-50"
                              >
                                <Icon path={ICONS.check} className="w-4 h-4" width={1.8} />
                                {reactivatingId === m.id ? "Reactivating…" : "Reactivate"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {membersPagination.totalPages > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Pagination page={membersPagination.page} totalPages={membersPagination.totalPages} onPageChange={membersPagination.setPage} />
              </div>
            )}
          </div>
        </>
      )}

      {modal?.type === "edit" && (
        <Modal title="Edit Member" onClose={() => setModal(null)} size="lg">
          <EditMemberForm
            member={modal.member}
            genders={genders}
            titles={titles}
            onCancel={() => setModal(null)}
            onSaved={onSaved}
          />
        </Modal>
      )}

      {modal?.type === "change-club" && (
        <ChangeMemberClubModal
          member={modal.member}
          clubs={clubs}
          onClose={() => setModal(null)}
          onDone={onSaved}
        />
      )}

      {modal?.type === "deactivate" && (
        <InactivateMemberModal
          member={modal.member}
          onClose={() => setModal(null)}
          onDone={onSaved}
        />
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
// Owns its own nested routes (list + per-member detail page, plus the
// per-process detail page for the "Pending Registrations" sub-tab) since
// ADMIN_NAV mounts it at "members/*".
export default function MembersManagement() {
  return (
    <Routes>
      <Route index element={<MembersManagementList />} />
      <Route path=":memberId" element={<MemberDetailPage />} />
      <Route path="pending-registrations/:processId" element={<MemberProcessDetailPage />} />
    </Routes>
  );
}
